/**
 * Activates a scroll-spy on a set of anchor links, toggling the "active" class
 * as the user scrolls past each corresponding section. Uses requestAnimationFrame
 * to throttle the scroll handler and avoid layout thrash on every tick.
 */
export function setupScrollSpy(
  links: HTMLAnchorElement[],
  signal: AbortSignal,
): void {
  const sections: HTMLElement[] = links.reduce<HTMLElement[]>((acc, link) => {
    const href = link.getAttribute("href");
    if (!href || !href.startsWith("#")) return acc;
    const section = document.querySelector<HTMLElement>(href);
    if (section) acc.push(section);
    return acc;
  }, []);

  if (sections.length === 0) return;

  let rafPending = false;

  const setActiveSection = (): void => {
    let currentSectionId = "";

    sections.forEach((section) => {
      const sectionTop = section.getBoundingClientRect().top + window.scrollY;
      const sectionHeight = section.offsetHeight;

      if (
        window.scrollY >= sectionTop - sectionHeight / 3 &&
        window.scrollY < sectionTop + sectionHeight - sectionHeight / 3
      ) {
        currentSectionId = section.id;
      }
    });

    links.forEach((link) => {
      const href = link.getAttribute("href");
      link.classList.toggle("active", !!href && href.substring(1) === currentSectionId);
    });

    rafPending = false;
  };

  setActiveSection();

  window.addEventListener(
    "scroll",
    () => {
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(setActiveSection);
      }
    },
    { signal, passive: true },
  );
}
